import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { convertUtc } from "../../../../utils/dateUtils";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { getParticipantDataFromSoap, createClaim, readDocument } from "../../api/ClaimAPI";
import { ClaimFormDto, ClaimPersonAffectedDto, ClaimProfesionalDto } from "../../types";
import { useServiceIndicatorSelect } from "../../../admin/Dictionaries/hooks/getServiceIndicatorSelectsByClient";
import { useFunctionalAreaSelect } from "../../../admin/Dictionaries/hooks/getFunctionalAreasByClient";
import { useClaimProfessionalTypeSelect } from "../../../admin/Dictionaries/hooks/getClaimProfessionalTypeSelectByClient";
import { ClaimShipmentStatusEnum } from "../../types/ClaimShipmentStatusEnum";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDemandInFormatSelect } from "../../../admin/Dictionaries/hooks/getDemandInFormatByClient";
import { useDemandOutFormatSelect } from "../../../admin/Dictionaries/hooks/getDemandOutFormatByClient";
import { useDemandDerivationSelect } from "../../../admin/Dictionaries/hooks/getDemandDerivationByClient";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import notesMedicalIcon from "@/assets/notes-medical-thin-full.svg";
import { useDispatch, useSelector, useStore } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { clearErrors, setClaimForm, setError } from "../../../../redux/claimFormSlice";
import { useNavigationGuard } from "../../../../contexts/NavigationGuardContext";
import { useClaimPersonalRelationshipSelect } from "../../../admin/Dictionaries/hooks/getPersonalRelationshipByClient";
import { useRequestTypesSelect } from "../../../admin/Dictionaries/hooks/useRequestTypesSelect";
import { useClaimOriginsSelect } from "../../../admin/Dictionaries/hooks/useClaimOriginsSelect";
import ClaimProfessionalsSection from "../common/ClaimProfessionalsSection";
import { useOrganizationalStructuresSelectByUser } from "../../../admin/Dictionaries/hooks/useOrganizationalStructuresSelect";
import { ClaimPersonTypeEnum } from "../../types/enums";
import dangerTriangleIcon from "@/assets/triangle-exclamation-solid-full.svg";
import successTriangleIcon from "@/assets/triangle-exclamation-solid-full.svg";
import ClaimsForPersonModal from "../modals/ClaimsForPersonModal";
import { SelectedFilter } from "../../../../components/Filters/types";
import { FilterTableKey, setSelectedFilters } from "../../../../redux/filtersSlice";
import { useGetClaimsForClaimant } from "../../hooks/useGetClaimsForClaimant";
import { useGetClaimsForAffectedPerson } from "../../hooks/useGetClaimsForAffectedPerson";

interface FormClaimProps {
    initialFile: File | null;
    setIsStepReadDocument: (isStepReadDocument: boolean) => void;
    setIsStepValidateFormData: (isStepValidateFormData: boolean) => void;
    setMessageError: (messageError: string) => void;
    readOnly: boolean;
    isStepReadDocument: boolean;
    isStepValidateFormData: boolean;
}

const RELATED_PEOPLE_DEBOUNCE_MS = 400;

export default function FormClaim({
    initialFile,
    setIsStepReadDocument,
    setIsStepValidateFormData,
    readOnly,
    isStepReadDocument,
    isStepValidateFormData,
    setMessageError
}: FormClaimProps) {
    const { t } = useTranslation('claim');
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const hasAccessToClinicalHistory = useSelector((state: RootState) => state.currentUserInfo.data.hasAccessToClinicalHistory);


    const claim = useSelector((state: RootState) => state.claimForm.data);
    const errors = useSelector((state: RootState) => state.claimForm.errors);

    const queryClient = useQueryClient();
    const [loading, setLoading] = useState(false);
    const [isReadDocument, setIsReadDocument] = useState<boolean>(isStepReadDocument);
    const [showClaimsForAffectedPersonModal, setShowClaimsForAffectedPersonModal] = useState(false);
    const [showClaimsForClaimantModal, setShowClaimsForClaimantModal] = useState(false);
    const { data: serviceIndicators } = useServiceIndicatorSelect({});
    const { data: functionalAreas } = useFunctionalAreaSelect({});
    const { data: claimPersonalRelationships } = useClaimPersonalRelationshipSelect({});
    const { data: claimProfessionalTypes } = useClaimProfessionalTypeSelect({});
    const { data: claimInOutFormats } = useDemandInFormatSelect({});
    const { data: claimRequestTypes } = useRequestTypesSelect({});
    const { data: claimOrigins } = useClaimOriginsSelect({});
    const { data: organizationalStructures } = useOrganizationalStructuresSelectByUser({});
    const [isSearchingAffectedPerson, setIsSearchingAffectedPerson] = useState(false);
    const [hasAffectedPersonSearchError, setHasAffectedPersonSearchError] = useState(false);
    const { setNavigationGuard } = useNavigationGuard();

    const [showModalCopyData, setShowModalCopyData] = useState(false);

    const { data: claimsForClaimant } = useGetClaimsForClaimant();
    const { data: claimsForAffectedPerson } = useGetClaimsForAffectedPerson();

    useEffect(() => {
        if (organizationalStructures?.length == 1) {
            handleChange("organizationalStructureId", organizationalStructures[0].id)
        }
    }, [organizationalStructures])

    useEffect(() => {
        if (!claim)
            return;

        const timeout = setTimeout(() => {
            searchRelatedPeople(ClaimPersonTypeEnum.Claimant);
        }, RELATED_PEOPLE_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [
        claim.nameAndSurname,
        claim.cip,
        claim.dni
    ]);

    useEffect(() => {
        if (!claim)
            return;

        const timeout = setTimeout(() => {
            searchRelatedPeople(ClaimPersonTypeEnum.Affected);
        }, RELATED_PEOPLE_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [
        claim.personAffected?.nameAndSurname,
        claim.personAffected?.cip,
        claim.personAffected?.dni,
        claim.personAffected?.medicalHistoryNumber
    ]);

    const createFilter = (name: string, value?: string | null): SelectedFilter => ({
        name,
        description: "",
        values: value ? [value] : [],
    });

    const searchRelatedPeople = async (type: ClaimPersonTypeEnum) => {
        const filters: SelectedFilter[] = [
            createFilter(
                "Name",
                type === ClaimPersonTypeEnum.Affected
                    ? claim?.personAffected?.nameAndSurname
                    : claim?.nameAndSurname
            ),
            createFilter(
                "CIP",
                type === ClaimPersonTypeEnum.Affected
                    ? claim?.personAffected?.cip
                    : claim?.cip
            ),
            createFilter(
                "DNI",
                type === ClaimPersonTypeEnum.Affected
                    ? claim?.personAffected?.dni
                    : claim?.dni
            ),
            createFilter(
                "MedicalRecordNumber",
                type === ClaimPersonTypeEnum.Affected
                    ? claim?.personAffected?.medicalHistoryNumber
                    : undefined
            ),
            createFilter("ClaimId", "0"),
        ];

        const table: FilterTableKey =
            type === ClaimPersonTypeEnum.Affected
                ? "claimsForPersonByAffectedPeople"
                : "claimsForPersonByClaimantClaims";

        dispatch(setSelectedFilters({
            table: table,
            filters: filters,
        }));
    }

    const setClaim = (updater: React.SetStateAction<ClaimFormDto>) => {
        const prevClaim = store.getState().claimForm.data;
        const nextClaim = typeof updater === "function"
            ? (updater as (prev: ClaimFormDto) => ClaimFormDto)(prevClaim)
            : updater;
        dispatch(setClaimForm(nextClaim));
    };

    const handleChange = (field: keyof ClaimFormDto, value: ClaimFormDto[keyof ClaimFormDto]) => {
        setClaim(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePersonAffectedChange = (
        field: keyof ClaimPersonAffectedDto,
        value: ClaimPersonAffectedDto[keyof ClaimPersonAffectedDto]
    ) => {
        setClaim(prev => ({
            ...prev,
            personAffected: {
                ...prev.personAffected,
                [field]: value
            }
        }));
    };

    const clearAffectedPersonData = () => {
        setClaim(prev => ({
            ...prev,
            personAffected: {
                ...prev.personAffected,
                nameAndSurname: "",
                birthDate: null,
                cip: "",
                medicalHistoryNumber: "",
                dni: "",
                address: "",
                city: "",
                zipCode: "",
                phone: "",
                mobile: "",
                email: ""
            }
        }));
    };

    const handleProfessionalsChange = (
        professionals: ClaimProfesionalDto[]
    ) => {
        setClaim(prev => ({
            ...prev!,
            professionals
        }));
    };

    const handleDateChange = (field: keyof ClaimFormDto, date: Date | null) => {
        if (date) {
            const utcDate = convertUtc(date);
            handleChange(field, utcDate!.getTime());
        } else {
            handleChange(field, null);
        }
    };

    const handlePersonAffectedDateChange = (
        field: keyof ClaimPersonAffectedDto,
        date: Date | null
    ) => {
        setClaim(prev => ({
            ...prev,
            personAffected: {
                ...prev.personAffected,
                [field]: (date ? convertUtc(date)!.getTime() : null)
            } as ClaimPersonAffectedDto
        }));
    };

    const handleSaveClaim = async () => {
        setLoading(true);
        dispatch(clearErrors());

        if (!claim?.organizationalStructureId) {
            dispatch(setError({ field: 'organizationalStructureId', message: t("common:form.requiredFieldForm") }))
            setMessageError(t("common:form.errorMandatoryFieldsToInform"));
            setLoading(false);
            return;
        }

        createClaim(claim, initialFile)
            .then((data: ClaimFormDto) => {
                loadData(data, data.professionals ?? []);
                setLoading(false);
                setIsStepValidateFormData(true);
                setIsStepReadDocument(true);

                queryClient.invalidateQueries({
                    queryKey: ['getHistoricClaims'],
                    exact: false,
                    refetchType: 'all',
                });
                queryClient.invalidateQueries({
                    queryKey: ['claimStatus'],
                    exact: false,
                    refetchType: 'all',
                });
                queryClient.invalidateQueries({
                    queryKey: ['ClaimStatusLineChartData'],
                    exact: false,
                    refetchType: 'all',
                });
                queryClient.invalidateQueries({
                    queryKey: ['ClaimServiceIndicatorBarChartData'],
                    exact: false,
                    refetchType: 'all',
                });
                queryClient.invalidateQueries({
                    queryKey: ['claimIndicatorsTimeTracking'],
                    exact: false,
                    refetchType: 'all',
                });
            })
            .catch((error) => {
                const fetchError = error as ApiErrorResponse;
                setMessageError(fetchError?.message || t("CommonError"))
                setLoading(false);
            });
    }

    useEffect(() => {
        setNavigationGuard({
            enabled: !isStepValidateFormData,
            message: t("claim:modal.modalExitWithoutSavingNewClaim"),
            messageButtonSave: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonSave"),
            messageButtonClose: t("surveyAdmin:form.modalExistWithUnsavedSurveyMessageButtonClose"),
            onSave: async () => {
                try {
                    await handleSaveClaim();
                    return true;
                } catch {
                    return false;
                }
            }
        });

        return () => {
            setNavigationGuard({
                enabled: false,
                message: "",
                messageButtonSave: "",
                messageButtonClose: ""
            });
        };
    }, [isStepValidateFormData,  setNavigationGuard, t]);


    const handleReadDocument = async () => {
        if (!initialFile)
            return;

        setLoading(true);
        readDocument(initialFile)
            .then(data => {
                loadData(data, data.professionals ?? []);
                setIsStepReadDocument(true);
            })
            .catch(error => {
                const fetchError = error as ApiErrorResponse;
                setMessageError(fetchError?.message || t("CommonError"));
            })
            .finally(() => {
                setLoading(false);
                setIsReadDocument(true);
            });
    }

    const loadData = (claim: ClaimFormDto, professionals: ClaimProfesionalDto[]) => {
        setClaim({
            ...claim,
            professionals: professionals,
            organizationalStructureId: organizationalStructures?.length == 1
                ? organizationalStructures[0].id
                : claim.organizationalStructureId
        });

    };

    const handleCopyData = () => {
        setClaim(prev => {
            const claimPersonAffectedDtoCopy: ClaimPersonAffectedDto = {
                nameAndSurname: prev.nameAndSurname,
                birthDate: prev.personAffected?.birthDate,
                cip: prev.cip,
                medicalHistoryNumber: prev.personAffected?.medicalHistoryNumber,
                dni: prev.dni,
                address: prev.address,
                city: prev.city,
                zipCode: prev.zipCode,
                phone: prev.phone,
                mobile: prev.mobile,
                email: prev.email
            };

            return {
                ...prev,
                personAffected: claimPersonAffectedDtoCopy
            };
        });

        setShowModalCopyData(false);
    }

    const validateCopyData = () => {
        const personAffected = claim?.personAffected;
        if (!!personAffected &&
            (!!personAffected.nameAndSurname ||
                !!personAffected.cip ||
                !!personAffected.dni ||
                !!personAffected.address ||
                !!personAffected.city ||
                !!personAffected.zipCode ||
                !!personAffected.phone ||
                !!personAffected.mobile ||
                !!personAffected.email
            )
        ) {
            setShowModalCopyData(true);
        } else {
            handleCopyData();
        }
    }

    const searchAffectedPerson = async () => {
        const medicalHistoryNumber = claim?.personAffected?.medicalHistoryNumber?.trim();
        if (isSearchingAffectedPerson) return;

        if (!medicalHistoryNumber) {
            setHasAffectedPersonSearchError(true);
            return;
        }

        setIsSearchingAffectedPerson(true);
        setHasAffectedPersonSearchError(false);
        try {
            const data = await getParticipantDataFromSoap(medicalHistoryNumber);
            const hasData = !!(
                data?.clinicalHistoryNumber ||
                data?.name ||
                data?.surname ||
                data?.cip ||
                data?.dni
            );

            if (!hasData) {
                clearAffectedPersonData();
                setHasAffectedPersonSearchError(true);
                setMessageError(t("affectedPersonNotFoundByMedicalHistory"));
                return;
            }

            handlePersonAffectedChange("medicalHistoryNumber", data.clinicalHistoryNumber);
            handlePersonAffectedChange("nameAndSurname", `${data.name} ${data.surname} ${data.surname2}`);
            if (data.birthDate) {
                const longBirthDate = new Date(data.birthDate).setHours(0, 0, 0, 0);
                handlePersonAffectedChange("birthDate", longBirthDate);
            }
            handlePersonAffectedChange("cip", data.cip);
            handlePersonAffectedChange("dni", data.dni);
            handlePersonAffectedChange("address", data.address);
            handlePersonAffectedChange("city", data.city);
            handlePersonAffectedChange("phone", data.phoneNumber);
            handlePersonAffectedChange("mobile", data.phoneNumber2);
            handlePersonAffectedChange("zipCode", data.zipCode);
            setHasAffectedPersonSearchError(false);
        } catch (err) {
            const fetchError = err as ApiErrorResponse;
            console.error(fetchError);
            clearAffectedPersonData();
            setHasAffectedPersonSearchError(true);
            setMessageError(fetchError?.message || t("affectedPersonNotFoundByMedicalHistory"));
        } finally {
            setIsSearchingAffectedPerson(false);
        }
    };

    const showRelatedPeopleClaims = (event: React.MouseEvent, type: ClaimPersonTypeEnum) => {
        event.stopPropagation();
        if (type == ClaimPersonTypeEnum.Affected)
            setShowClaimsForAffectedPersonModal(true);
        else
            setShowClaimsForClaimantModal(true);
    }

    return (<>
        {loading && (
            <div className="overlay-spinner">
                <Spinner animation="border" variant="light" />
            </div>
        )}
        <ModalGlobal
            show={showModalCopyData}
            onClose={() => setShowModalCopyData(false)}
            body={t("claim:modal.modalCopyDataTitle")}
            onSave={handleCopyData}
            saveText={t("common:buttons.buttonYes")}
            closeText={t("common:buttons.buttonNo")}
            isLoading={false}
        />
        {claimsForAffectedPerson && claimsForAffectedPerson.items != null && (
            <ClaimsForPersonModal
                show={showClaimsForAffectedPersonModal}
                claimsForPerson={claimsForAffectedPerson || []}
                onClose={() => setShowClaimsForAffectedPersonModal(false)}
                paginationType="claimsForPersonByAffectedPeople"
                />)}
        {claimsForClaimant && claimsForClaimant.items != null && (
            <ClaimsForPersonModal
                show={showClaimsForClaimantModal}
                claimsForPerson={claimsForClaimant || []}
                onClose={() => setShowClaimsForClaimantModal(false)}
                paginationType="claimsForPersonByClaimantClaims"
            />
        )}
        <Row>
            <Col>
                <Form>
                    <Row className="mt-3">
                        <Col className="d-flex justify-content-end" >
                            {!readOnly && (<Button variant="secondary" onClick={handleSaveClaim}>{t("claim:createClaimButton")}</Button>)}
                            {!isReadDocument && !readOnly && (<Button variant="primary" className="ms-2" onClick={handleReadDocument}>{t("claim:readClaimFileButton")}</Button>)}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>{t("claim:newClaimInputType")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.requestTypeId}
                                disabled={readOnly}
                                onChange={(e) => handleChange("requestTypeId", parseInt(e.target.value))}
                            >
                                {claimRequestTypes?.map((claimRequestType) => (
                                    <option key={claimRequestType.id} value={claimRequestType.id}>
                                        {claimRequestType.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <InputDatePicker
                                value={claim?.eventDate ? new Date(claim.eventDate) : null}
                                onChange={(date) => handleDateChange("eventDate", date)}
                                label={t("claim:newClaimInputEventDate")}
                                disabled={readOnly}
                            />
                        </Col>
                        <Col xs={6} md={6}>
                            <InputDatePicker
                                value={claim?.entryDate ? new Date(claim.entryDate) : null}
                                onChange={(date) => handleDateChange("entryDate", date)}
                                label={t("claim:newClaimInputOacEntryDate")}
                                disabled={readOnly}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} md={12} className="mt-4 d-flex">
                            <h5>{t("claim:headerDataPerson")}</h5>
                            { claimsForClaimant != null && claimsForClaimant.totalRecords > 0 && (
                                        claimsForClaimant.hasClaimOpenOrInInProgress ?
                                 <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Claimant)}>
                                    <img
                                        title={t("claim:activeRequestsForPersonMessage")}
                                        alt={t("claim:activeRequestsForPersonMessage")}
                                        src={dangerTriangleIcon}
                                        width={30}
                                        height={30}
                                    />
                                </div>
                                : <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Claimant)}>
                                    <img
                                        title={t("claim:requestsForPersonMessage")}
                                        alt={t("claim:requestsForPersonMessage")}
                                        src={successTriangleIcon}
                                        width={40}
                                        height={40}
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>
                     <Row>
                        <Col xs={12} md={12}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputNameAndSurname")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.nameAndSurname ?? ""}
                                    onChange={(e) => handleChange("nameAndSurname", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>CIP</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.cip ?? ""}
                                    onChange={(e) => handleChange("cip", e.target.value)}
                                />

                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputNameAndDni")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.dni ?? ""}
                                    onChange={(e) => handleChange("dni", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputAddress")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.address ?? ""}
                                    onChange={(e) => handleChange("address", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputZipCode")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.zipCode ?? ""}
                                    onChange={(e) => handleChange("zipCode", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputCity")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.city ?? ""}
                                    onChange={(e) => handleChange("city", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPhone")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.phone ?? ""}
                                    onChange={(e) => handleChange("phone", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputMobile")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.mobile ?? ""}
                                    onChange={(e) => handleChange("mobile", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputEmail")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.email ?? ""}
                                    onChange={(e) => handleChange("email", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                         <Col xs={6} md={6}>
                            <Form.Label>{t("claim:newClaimInputRelationshipWithAffected")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.relationshipWithAffectedPersonId ?? ""}
                                disabled={readOnly}
                                onChange={(e) => handleChange("relationshipWithAffectedPersonId", parseInt(e.target.value))}>
                                <option value=""></option>
                                {claimPersonalRelationships?.map((claimPersonalRelationship) => (
                                    <option key={claimPersonalRelationship.id} value={claimPersonalRelationship.id}>
                                        {claimPersonalRelationship.name}
                                    </option>
                                ))}

                            </Form.Select>
                        </Col>
                    </Row>
                    <Row className="align-items-center">
                        <Col xs={8} md={8} className="d-flex mt-4">
                            <h5>{t("claim:headerDataAffectedPerson")}</h5>
                            {claimsForAffectedPerson != null && claimsForAffectedPerson.totalRecords > 0 && (
                                claimsForAffectedPerson.hasClaimOpenOrInInProgress 
                                ? <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Affected)}>
                                    <img
                                        title={t("claim:activeRequestsForPersonMessage")}
                                        alt={t("claim:activeRequestsForPersonMessage")}
                                        src={dangerTriangleIcon}
                                        width={30}
                                        height={30}
                                    />
                                </div>
                                : <div className="ms-2 cursor-pointer" onClick={(event) => showRelatedPeopleClaims(event, ClaimPersonTypeEnum.Affected)}>
                                    <img
                                        title={t("claim:requestsForPersonMessage")}
                                        alt={t("claim:requestsForPersonMessage")}
                                        src={successTriangleIcon}
                                        width={40}
                                        height={40}
                                    />
                                </div>
                            )}
                        </Col>
                        <Col xs={4} md={4} className="mt-4 text-end">
                            <Button variant="secondary" onClick={validateCopyData}>
                                {t("claim:buttonCopyDades")}
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputNameAndSurnameAffected")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.nameAndSurname ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("nameAndSurname", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>CIP</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.cip ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("cip", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputNameAndDni")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.dni ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("dni", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimMedicalHistoryNumber")}</Form.Label>
                                <div className="input-calendar">
                                    <Form.Control
                                        type="text"
                                        readOnly={readOnly}
                                        value={claim?.personAffected?.medicalHistoryNumber ?? ""}
                                        onChange={(e) => {
                                            handlePersonAffectedChange("medicalHistoryNumber", e.target.value);
                                            if (hasAffectedPersonSearchError) setHasAffectedPersonSearchError(false);
                                        }}
                                        isInvalid={hasAffectedPersonSearchError}
                                    />
                                    {hasAccessToClinicalHistory && 
                                        <span className="content-icon-calendar"
                                            onClick={searchAffectedPerson}
                                            style={{ cursor: isSearchingAffectedPerson ? "default" : "pointer" }}
                                        >
                                            {isSearchingAffectedPerson ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <img
                                                    style={{ marginRight: "5px", filter: "invert(18%) sepia(99%) saturate(1200%) hue-rotate(185deg) brightness(85%)" }}
                                                    title={t("common:toggles.searchByClinicHistoryNumber")}
                                                    src={notesMedicalIcon}
                                                    alt={t("common:toggles.searchByClinicHistoryNumber")}
                                                    width={25}
                                                    height={25}
                                                />
                                            )}
                                        </span>
                                    }
                                </div>
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <InputDatePicker
                                value={
                                    claim?.personAffected?.birthDate
                                        ? new Date(claim.personAffected.birthDate)
                                        : null
                                }
                                onChange={(date) =>
                                    handlePersonAffectedDateChange("birthDate", date)
                                }
                                label={t("claim:newClaimInputPersonAffectedBirthDate")}
                                disabled={readOnly}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedAddress")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.address ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("address", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedZipCode")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.zipCode ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("zipCode", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedCity")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.city ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("city", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedPhone")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.phone ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("phone", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedMobile")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.mobile ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("mobile", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputPersonAffectedEmail")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.personAffected?.email ?? ""}
                                    onChange={(e) => handlePersonAffectedChange("email", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12} className="mt-4">
                            <h5>{t("claim:headerDataReason")}</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12}>
                            <Form.Label>{t("claim:newClaimInputComment")}</Form.Label>
                            <Form.Control
                                as="textarea"
                                readOnly={readOnly}
                                rows={5}
                                value={claim?.comment ?? ""}
                                onChange={(e) => handleChange("comment", e.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Label>{t("organizationalStructure:organizationalStructure")}*</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={
                                    claim?.organizationalStructureId
                                    ?? ""
                                }
                                disabled={readOnly}
                                onChange={(e) => handleChange("organizationalStructureId", parseInt(e.target.value))}
                                isInvalid={!!errors.organizationalStructureId}
                            >
                                <option value=""></option>
                                {organizationalStructures?.map((organizationalStructure) => (
                                    <option key={organizationalStructure.id} value={organizationalStructure.id}>
                                        {'\u00A0'.repeat(organizationalStructure.level * 4)}{organizationalStructure.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.organizationalStructureId}
                            </Form.Control.Feedback>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Label>{t("claim:newClaimFunctionalArea")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.functionalAreaId ?? ""}
                                disabled={readOnly}
                                onChange={(e) => handleChange("functionalAreaId", parseInt(e.target.value))}
                            >
                                <option value=""></option>
                                {functionalAreas?.map((functionalArea) => (
                                    <option key={functionalArea.id} value={functionalArea.id}>
                                        {functionalArea.name}
                                    </option>
                                ))}

                            </Form.Select>
                        </Col>
                        {/* No se debe mostrar el número de habitación segun el ticket http://gitsrv/Medidedalia/Qualia_Documentaci%C3%B3/_boards/board/t/Qualia_Documentaci%C3%B3%20Team/Stories/?workitem=2267 */}
                        {/* <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputRoomNumber")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.roomNumber ?? ""}
                                    onChange={(e) => handleChange("roomNumber", e.target.value)}
                                />
                            </Form.Group>
                        </Col> */}
                    </Row>
                    <Row>
                        <Col xs={12} sm={12} md={12}>
                            <Form.Label>{t("claim:newClaimServiceIndicator")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.serviceIndicatorId ?? ""}
                                disabled={readOnly}
                                onChange={(e) => handleChange("serviceIndicatorId", parseInt(e.target.value))}
                            >
                                <option value=""></option>
                                {serviceIndicators?.map((serviceIndicator) => (
                                    <option key={serviceIndicator.id} value={serviceIndicator.id}>
                                        {serviceIndicator.name}
                                    </option>
                                ))}

                            </Form.Select>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} md={12} className="mt-4">
                            <h5>{t("claim:headerProfessionalData")}</h5>
                        </Col>
                    </Row>
                    <div className="pl-3">
                        <ClaimProfessionalsSection
                            professionals={claim.professionals ?? []}
                            readOnly={readOnly}
                            serviceIndicators={serviceIndicators}
                            claimProfessionalTypes={claimProfessionalTypes}
                            onProfessionalsChange={handleProfessionalsChange}
                        />
                    </div>
                    <Row>
                        <Col xs={12} md={12} className="mt-4">
                            <h5>{t("claim:headerInternalData")}</h5>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputRegisterNumber")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.registerNumber ?? ""}
                                    onChange={(e) => handleChange("registerNumber", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={6}>
                            <InputDatePicker
                                value={claim?.registerDate ? new Date(claim.registerDate) : null}
                                onChange={(date) => handleDateChange("registerDate", date)}
                                label={t("claim:newClaimInputRegisterDate")}
                                disabled={readOnly}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} md={6}>
                            <Form.Label>{t("claim:newClaimInputFormat")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.claimInOutFormatInputId ?? ""}
                                disabled={readOnly}
                                onChange={(e) => handleChange("claimInOutFormatInputId", parseInt(e.target.value))}
                            >
                                <option value=""></option>
                                {[...(claimInOutFormats ?? [])]
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        
                    </Row>
                    <Row className="mb-3">
                        <Col xs={6} md={6}>
                            <Form.Label>{t("claim:claimOrigins")}</Form.Label>
                            <Form.Select
                                className="form-control"
                                value={claim?.originId ?? ""}
                                disabled={readOnly}
                                onChange={(e) => handleChange("originId", parseInt(e.target.value))}
                            >
                                <option value=""></option>
                                {claimOrigins?.map((origin) => (
                                    <option key={origin.id} value={origin.id}>
                                        {origin.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={6}>
                            <Form.Group>
                                <Form.Label>{t("claim:newClaimInputOtherOrigin")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly={readOnly}
                                    value={claim?.otherOrigin ?? ""}
                                    onChange={(e) => handleChange("otherOrigin", e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    </>)
};
